import React, { useMemo, useState } from "react"
import { Box, Button, Checkbox, Divider, IconButton, InputBase, Popover, Typography } from "@mui/material"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import moment from "moment"

import { COLORS } from "../../../utils"
import EnterpriseBadge from "../../../components/Organizations/EnterpriseBadge"

const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"]
const WEEKDAYS_FR = ["L", "M", "M", "J", "V", "S", "D"]
const RANGE_BG = "#E3F2FD"

const formatShort = (m) => m.format("D MMM.")
const formatLong  = (m) => m.format("D MMM. YYYY")

const DeliveryMenusFilter = ({ enterprises, enterpriseFilter, dateFrom, dateTo, onEnterpriseChange, onDateFromChange, onDateToChange }) => {
  const [enterpriseAnchorEl, setEnterpriseAnchorEl] = useState(null)
  const [enterpriseSearch, setEnterpriseSearch] = useState("")
  const [enterpriseDraft, setEnterpriseDraft] = useState([])
  const [dateAnchorEl, setDateAnchorEl] = useState(null)
  const [calendarView, setCalendarView] = useState({ year: moment().year(), month: moment().month() })

  const activeEnterprises = useMemo(() => enterprises.filter((e) => e.status !== "ARCHIVED"), [enterprises])

  const enterpriseSearched = useMemo(
    () => activeEnterprises.filter((e) => e.name.toLowerCase().includes(enterpriseSearch.toLowerCase())),
    [activeEnterprises, enterpriseSearch]
  )

  const handleEnterpriseOpen = (e) => { setEnterpriseDraft([...enterpriseFilter]); setEnterpriseSearch(""); setEnterpriseAnchorEl(e.currentTarget) }
  const handleEnterpriseClose = () => setEnterpriseAnchorEl(null)
  const handleEnterpriseSave = () => { onEnterpriseChange(enterpriseDraft); setEnterpriseAnchorEl(null) }
  const handleEnterpriseReset = () => setEnterpriseDraft([])
  const handleToggle = (id) => setEnterpriseDraft((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  const handleSelectAll = () => {
    const searchedIds = enterpriseSearched.map((e) => e.objectId)
    const allSelected = searchedIds.length > 0 && searchedIds.every((id) => enterpriseDraft.includes(id))
    if (allSelected) {
      setEnterpriseDraft((prev) => prev.filter((id) => !searchedIds.includes(id)))
    } else {
      setEnterpriseDraft((prev) => [...new Set([...prev, ...searchedIds])])
    }
  }

  const handleDateOpen = (e) => {
    const base = dateFrom || moment()
    setCalendarView({ year: base.year(), month: base.month() })
    setDateAnchorEl(e.currentTarget)
  }

  const navMonth = (delta) => {
    setCalendarView((v) => {
      const next = moment({ year: v.year, month: v.month }).add(delta, "month")
      return { year: next.year(), month: next.month() }
    })
  }

  const onPickDay = (d) => {
    const hasStart = !!dateFrom
    const hasEnd = !!dateTo
    if (!hasStart || (hasStart && hasEnd)) {
      onDateFromChange(d.clone().startOf("day"))
      onDateToChange(null)
    } else {
      const end = d.isBefore(dateFrom, "day") ? dateFrom.clone().endOf("day") : d.clone().endOf("day")
      const start = d.isBefore(dateFrom, "day") ? d.clone().startOf("day") : dateFrom
      onDateFromChange(start)
      onDateToChange(end)
      setDateAnchorEl(null)
    }
  }

  const firstOfMonth = moment({ year: calendarView.year, month: calendarView.month, day: 1 })
  const startWeekday = (firstOfMonth.day() + 6) % 7
  const cells = []
  for (let i = 0; i < startWeekday; i++) cells.push(null)
  for (let d = 1; d <= firstOfMonth.daysInMonth(); d++) cells.push(moment({ year: calendarView.year, month: calendarView.month, day: d }))
  while (cells.length % 7 !== 0) cells.push(null)

  const hasDateFilter = !!(dateFrom || dateTo)
  const today = moment().startOf("day")

  const rangeLabel = (() => {
    if (!dateFrom && !dateTo) return null
    if (dateFrom && dateTo) return `${formatShort(dateFrom)} → ${formatLong(dateTo)}`
    if (dateFrom) return `À partir du ${formatLong(dateFrom)}`
    return `Jusqu'au ${formatLong(dateTo)}`
  })()

  return (
    <>
      <Box className="flexRow center" sx={sx.filters}>
        <Box component="button" type="button" onClick={handleEnterpriseOpen} sx={{ ...sx.filterBtn, ...(enterpriseFilter.length > 0 && sx.filterBtnActive) }} aria-haspopup="listbox">
          <Box component="span">Entreprise</Box>
          {enterpriseFilter.length > 0 && <Box component="span" sx={sx.epBadge}>{enterpriseFilter.length}</Box>}
          <ExpandMoreIcon sx={{ fontSize: 18, color: "inherit", flexShrink: 0 }} />
        </Box>

        <Box component="button" type="button" onClick={handleDateOpen} sx={{ ...sx.filterBtn, ...(hasDateFilter && sx.filterBtnActive) }} aria-label="Sélectionner une période">
          <CalendarMonthIcon sx={{ fontSize: 20, flexShrink: 0 }} />
          <Box component="span">Jour de livraison&nbsp;:</Box>
          {rangeLabel
            ? <Box component="span" sx={sx.dateBtnRange}>{rangeLabel}</Box>
            : <Box component="span" sx={sx.dateBtnPlaceholder}>Toutes les dates</Box>}
        </Box>
      </Box>

      {/* Date range popover */}
      <Popover
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        onClose={() => setDateAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 2, display: "flex", flexDirection: "column", gap: 1.5, width: 300 } } }}
      >
        {/* Month navigation */}
        <Box className="flexRow center spaceBetween">
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.BLACK, textTransform: "capitalize" }}>
            {MONTHS_FR[calendarView.month]} {calendarView.year}
          </Typography>
          <Box className="flexRow" sx={{ gap: 0.5 }}>
            <IconButton size="small" onClick={() => navMonth(-1)}><ChevronLeftIcon sx={{ fontSize: 18, color: COLORS.DARK_GREY }} /></IconButton>
            <IconButton size="small" onClick={() => navMonth(1)}><ChevronRightIcon sx={{ fontSize: 18, color: COLORS.DARK_GREY }} /></IconButton>
          </Box>
        </Box>

        {/* Weekday headers */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {WEEKDAYS_FR.map((w, i) => (
            <Box key={i} sx={{ textAlign: "center", fontSize: 12, color: COLORS.DISABLED, py: "4px" }}>{w}</Box>
          ))}
        </Box>

        {/* Day cells */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {cells.map((d, i) => {
            if (!d) return <Box key={i} sx={{ aspectRatio: "1" }} />
            const isStart = dateFrom && d.isSame(dateFrom, "day")
            const isEnd = dateTo && d.isSame(dateTo, "day")
            const isSelected = isStart || isEnd
            const isInRange = dateFrom && dateTo && d.isAfter(dateFrom, "day") && d.isBefore(dateTo, "day")
            const isRangeStart = isStart && !!dateTo
            const isRangeEnd = isEnd && !!dateFrom
            const showStrip = isInRange || isRangeStart || isRangeEnd
            return (
              <Box
                key={i}
                onClick={() => onPickDay(d)}
                sx={{
                  aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center",
                  position: "relative", cursor: "pointer",
                  "&::before": {
                    // eslint-disable-next-line quotes
                    content: '""', position: "absolute", top: "6px", bottom: "6px",
                    left: isRangeStart ? "50%" : 0,
                    right: isRangeEnd ? "50%" : 0,
                    background: showStrip ? RANGE_BG : "transparent",
                    zIndex: 1,
                  },
                  "&:hover > div": { bgcolor: isSelected ? COLORS.PRIMARY_COLOR : "rgba(0,0,0,0.06)" },
                }}
              >
                <Box sx={{
                  width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  zIndex: 2, position: "relative", fontSize: 13,
                  color: isSelected ? COLORS.WHITE : COLORS.BLACK,
                  bgcolor: isSelected ? COLORS.PRIMARY_COLOR : "transparent",
                  fontWeight: isSelected ? 600 : 400,
                  border: d.isSame(today, "day") && !isSelected ? `1px solid ${COLORS.DEFAULT_GREY}` : "none",
                  transition: "background 120ms",
                }}>
                  {d.date()}
                </Box>
              </Box>
            )
          })}
        </Box>

        <Divider />

        {/* Presets */}
        <Box className="flexRow wrap" sx={sx.presets}>
          {[
            { label: "Aujourd'hui",      from: moment().startOf("day"),     to: moment().endOf("day") },
            { label: "Cette semaine",    from: moment().startOf("isoWeek"), to: moment().endOf("isoWeek") },
            { label: "Semaine dernière", from: moment().subtract(1, "week").startOf("isoWeek"), to: moment().subtract(1, "week").endOf("isoWeek") },
            { label: "Ce mois",          from: moment().startOf("month"),   to: moment().endOf("month") },
            { label: "Ce trimestre",     from: moment().startOf("quarter"), to: moment().endOf("quarter") },
            { label: "Année en cours",   from: moment().startOf("year"),    to: moment().endOf("year") },
          ].map(({ label, from, to }) => {
            const active = dateFrom && dateTo && dateFrom.isSame(from, "day") && dateTo.isSame(to, "day")
            return (
              <Box key={label} component="button" type="button"
                onClick={() => { onDateFromChange(from); onDateToChange(to); setDateAnchorEl(null) }}
                sx={{ ...sx.preset, ...(active && sx.presetActive) }}
              >
                {label}
              </Box>
            )
          })}
        </Box>

        {hasDateFilter && (
          <Button variant="text" size="small" onClick={() => { onDateFromChange(null); onDateToChange(null) }} sx={{ alignSelf: "flex-start", px: 0, mt: -1 }}>
            Réinitialiser
          </Button>
        )}
      </Popover>

      {/* Enterprise popover */}
      <Popover
        open={Boolean(enterpriseAnchorEl)}
        anchorEl={enterpriseAnchorEl}
        onClose={handleEnterpriseClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: sx.epPaper } }}
      >
        <Box className="flexRow center" sx={sx.epSearch}>
          <SearchIcon sx={sx.epSearchIcon} />
          <InputBase value={enterpriseSearch} onChange={(e) => setEnterpriseSearch(e.target.value)} placeholder="Rechercher une entreprise…" className="flex1" sx={{ fontSize: 14 }} autoFocus />
        </Box>
        <Divider />
        <Box className="flexRow center spaceBetween" sx={sx.epToolbar}>
          <Button variant="text" size="small" onClick={handleSelectAll} sx={sx.epSelectAll}>Tout sélectionner</Button>
          <Typography sx={sx.epCount}>{enterpriseDraft.length}/{activeEnterprises.length}</Typography>
        </Box>
        <Divider />
        {enterpriseSearched.length > 0 ? (
          <Box sx={sx.epList} role="listbox" aria-multiselectable="true">
            {enterpriseSearched.map((e) => (
              <Box key={e.objectId} component="label" className="flexRow center pointer" sx={sx.epItem}>
                <Checkbox size="small" checked={enterpriseDraft.includes(e.objectId)} onChange={() => handleToggle(e.objectId)} sx={{ p: "4px" }} />
                <EnterpriseBadge enterprise={e} size={20} />
                <Typography sx={sx.epItemName}>{e.name}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={sx.epEmpty}>Aucun résultat</Typography>
        )}
        <Divider />
        <Box className="flexRow justifyEnd" sx={sx.epFooter}>
          <Button variant="outlined" size="small" onClick={handleEnterpriseReset}>Réinitialiser</Button>
          <Button variant="contained" size="small" onClick={handleEnterpriseSave}>Enregistrer</Button>
        </Box>
      </Popover>
    </>
  )
}

const sx = {
  filters: { px: 3, pt: 2, pb: 1, gap: 1 },
  filterBtn: {
    height: 36, px: "12px", pl: "8px", gap: "6px",
    border: "1px solid", borderColor: "rgba(0,0,0,0.06)", borderRadius: "4px",
    bgcolor: COLORS.WHITE, fontFamily: "inherit", fontSize: 14, color: COLORS.DARK_GREY,
    cursor: "pointer", display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
    transition: "border-color 150ms, color 150ms",
    "&:hover": { borderColor: COLORS.DARK_GREY },
  },
  filterBtnActive: { borderColor: COLORS.PRIMARY_COLOR, color: COLORS.BLACK },
  dateBtnRange: { fontWeight: 500, color: COLORS.PRIMARY_COLOR },
  dateBtnPlaceholder: { color: COLORS.DISABLED },
  epBadge: { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 20, height: 20, borderRadius: "50%", bgcolor: COLORS.PRIMARY_COLOR, color: COLORS.WHITE, fontSize: 11, fontWeight: 700, px: "4px" },
  epPaper: { width: 280, maxHeight: 380, display: "flex", flexDirection: "column" },
  epSearch: { gap: "6px", px: 1.5, py: 1 },
  epSearchIcon: { fontSize: 18, color: COLORS.DISABLED, flexShrink: 0 },
  epToolbar: { px: 1.5, py: 0.5 },
  epSelectAll: { fontSize: 13, p: 0, minWidth: 0, textTransform: "none" },
  epCount: { fontSize: 13, color: COLORS.DARK_GREY },
  epList: { overflowY: "auto", flex: 1 },
  epItem: { gap: "4px", px: 1, py: "2px", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } },
  epItemName: { fontSize: 14, color: COLORS.BLACK, lineHeight: 1.4 },
  epEmpty: { fontSize: 14, color: COLORS.DISABLED, textAlign: "center", py: 3, px: 2 },
  epFooter: { gap: 1, px: 1.5, py: 1 },
  presets: { gap: "6px" },
  preset: {
    height: 26, px: "10px", border: "1px solid", borderColor: COLORS.DEFAULT_GREY, borderRadius: "14px",
    bgcolor: "transparent", fontFamily: "inherit", fontSize: 12, color: COLORS.DARK_GREY,
    cursor: "pointer", whiteSpace: "nowrap",
    transition: "border-color 120ms, background-color 120ms, color 120ms",
    "&:hover": { borderColor: COLORS.PRIMARY_COLOR, color: COLORS.PRIMARY_COLOR },
  },
  presetActive: { borderColor: COLORS.PRIMARY_COLOR, bgcolor: COLORS.PRIMARY_COLOR, color: COLORS.WHITE, "&:hover": { bgcolor: COLORS.PRIMARY_COLOR, color: COLORS.WHITE } },
}

export default DeliveryMenusFilter
